import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateSellerPayload, SellerFilters, UpdateSellerPayload } from '../types/seller.types'
import { addWhitelistedSeller } from '../api/react/organizations/organizations'
import {
  abortSellerItemExport,
  addWhitelistedSellerForMember,
  addWhitelistedSellerGlobal,
  bulkAddSellersToWhitelist,
  bulkCreateSellers,
  createSeller,
  createSellerComment,
  createSellerItemExport,
  deleteSellerComment,
  editSeller,
  getSellerByIdExpanded,
  getSellerComments,
  getSellerItemExportStatus,
  getSellers,
  getSellersCount,
  mergeSellers,
  removeWhitelistedSellerForBrand,
  removeWhitelistedSellerForMember,
  removeWhitelistedSellerGlobal,
  setSellerUnderInvestigation,
  updateSellerComment,
} from '../api/react/sellers/Sellers'
import type { BulkAddSellersToWhitelistPayload, CreateSellerCommentPayload, SellerItemExportPayload, SetUnderInvestigationPayload } from '../types/seller.types'

export type SellersFilters = SellerFilters

export const SELLER_KEYS = {
  all: ['sellers'] as const,
  list: (filters?: SellersFilters) => ['sellers', filters] as const,
  paginated: (filters: SellersFilters, page: number, pageSize: number) =>
    ['sellers', filters, 'paginated', page, pageSize] as const,
  infinite: (filters: SellersFilters) => ['sellers', filters, 'infinite'] as const,
  count: (filters: SellersFilters) => ['sellers', filters, 'count'] as const,
  detail: (id: string | undefined) => ['sellers', id, 'detail'] as const,
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export const useGetSellers = (filters: SellersFilters, enabled = true) => {
  return useQuery({
    queryKey: SELLER_KEYS.list(filters),
    queryFn: () => getSellers(filters),
    enabled,
  })
}

export const useSellersPaginated = (
  filters: SellersFilters,
  page: number,
  pageSize: number,
  enabled = true
) => {
  return useQuery({
    queryKey: SELLER_KEYS.paginated(filters, page, pageSize),
    queryFn: () => getSellers({ ...filters, page, page_size: pageSize }),
    enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  })
}

export const usePrefetchSellersPaginated = () => {
  const qc = useQueryClient()
  return (filters: SellersFilters, page: number, pageSize: number) =>
    qc.prefetchQuery({
      queryKey: SELLER_KEYS.paginated(filters, page, pageSize),
      queryFn: () => getSellers({ ...filters, page, page_size: pageSize }),
      staleTime: 5 * 60 * 1000,
    })
}

export const useSellersInfinite = (filters: SellersFilters, enabled = true) => {
  return useInfiniteQuery({
    queryKey: SELLER_KEYS.infinite(filters),
    queryFn: ({ pageParam = null }) =>
      getSellers({ ...filters, page: pageParam as number | null, page_size: 50 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.nextPage !== null ? lastPage.nextPage : undefined),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

export const useSellerCount = (filters: SellersFilters) => {
  return useQuery({
    queryKey: SELLER_KEYS.count(filters),
    queryFn: () => getSellersCount(filters),
  })
}

export const useSellerByIdExpanded = (
  id: string | undefined,
  expand: string,
  options?: { pollWhileScraping?: boolean; pollIntervalMs?: number },
) => {
  const pollWhileScraping = options?.pollWhileScraping ?? false
  const pollIntervalMs = options?.pollIntervalMs ?? 4000

  return useQuery({
    queryKey: SELLER_KEYS.detail(id),
    queryFn: () => getSellerByIdExpanded({ seller_id: id!, expand }),
    enabled: !!id,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always' as const,
    refetchOnReconnect: false,
    refetchInterval: (query) => {
      if (!pollWhileScraping) return false
      const data = query.state.data as
        | { scrape_details_status?: number; scrape_items_status?: number }
        | null
        | undefined
      const detailsStatus = data?.scrape_details_status
      const itemsStatus = data?.scrape_items_status
      const detailsActive = detailsStatus === 1 || detailsStatus === 2
      const itemsActive = itemsStatus === 1 || itemsStatus === 2
      return detailsActive || itemsActive ? pollIntervalMs : false
    },
  })
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export const useCreateSeller = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateSellerPayload) => createSeller(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: SELLER_KEYS.all }),
  })
}

export const useEditSeller = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateSellerPayload) => editSeller(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: SELLER_KEYS.all }),
  })
}

export const useRequestSellerCrawl = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sellerId: string) =>
      editSeller({ id: sellerId, scrape_details_status: 1 }),
    onSuccess: (_data, sellerId) => {
      qc.invalidateQueries({ queryKey: SELLER_KEYS.detail(sellerId) })
      qc.invalidateQueries({ queryKey: SELLER_KEYS.all })
    },
  })
}

export const useRequestSellerItemsScrape = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sellerId: string) =>
      editSeller({ id: sellerId, scrape_items_status: 1 }),
    onSuccess: (_data, sellerId) => {
      qc.invalidateQueries({ queryKey: SELLER_KEYS.detail(sellerId) })
      qc.invalidateQueries({ queryKey: SELLER_KEYS.all })
    },
  })
}

export const useMergeSellers = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sellerIds: string[]) => mergeSellers(sellerIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: SELLER_KEYS.all }),
  })
}

export const useWhitelistSellers = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sellerIds: string[]) =>
      Promise.all(sellerIds.map((id) => addWhitelistedSellerGlobal(id))),
    onSuccess: () => qc.invalidateQueries({ queryKey: SELLER_KEYS.all }),
  })
}

export const useAddWhitelistedSellerForMember = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ memberId, sellerId }: { memberId: string; sellerId: string }) =>
      addWhitelistedSellerForMember(memberId, sellerId),
    onSuccess: () => qc.invalidateQueries({ queryKey: SELLER_KEYS.all }),
  })
}

export const useBulkAddSellersToWhitelist = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: BulkAddSellersToWhitelistPayload) => bulkAddSellersToWhitelist(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: SELLER_KEYS.all }),
  })
}

export const useWhitelistSeller = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      sellerId,
      scope,
      memberId,
      brandIds,
    }: {
      sellerId: string
      scope: 'global' | 'member' | 'brand'
      memberId?: string
      brandIds?: string[]
    }) => {
      if (scope === 'global') {
        return addWhitelistedSellerGlobal(sellerId)
      }
      if (scope === 'member') {
        if (!memberId) throw new Error('Select a member to continue.')
        return addWhitelistedSellerForMember(memberId, sellerId)
      }
      if (scope === 'brand') {
        if (!brandIds?.length) throw new Error('Select at least one brand.')
        await Promise.all(brandIds.map((brandId) => addWhitelistedSeller(brandId, sellerId)))
        return
      }
      throw new Error('Select a scope to continue.')
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SELLER_KEYS.all }),
  })
}

export const useBulkWhitelistSellers = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      sellers,
      scope,
      memberId,
      brandIds,
      onProgress,
    }: {
      sellers: string[]
      scope: 'global' | 'member' | 'brand'
      memberId?: string
      brandIds: string[]
      onProgress?: (completed: number, total: number) => void
    }) => {
      if (scope === 'brand' && brandIds.length > 1) {
        onProgress?.(0, sellers.length * brandIds.length)
        const results: { brand: string; success: boolean; error?: unknown }[] = []
        for (let i = 0; i < brandIds.length; i++) {
          try {
            await bulkAddSellersToWhitelist({ sellers, scope, brandIds: [brandIds[i]] })
            results.push({ brand: brandIds[i], success: true })
          } catch (error) {
            results.push({ brand: brandIds[i], success: false, error })
          }
          onProgress?.((i + 1) * sellers.length, sellers.length * brandIds.length)
        }
        const failureCount = results.filter((r) => !r.success).length
        if (failureCount > 0) throw new Error(`${failureCount} of ${results.length} brand bulk operations failed`)
        return { sellers, successCount: results.length, totalSellers: sellers.length }
      }
      return bulkAddSellersToWhitelist({
        sellers,
        scope,
        ...(scope === 'member' && memberId && { memberId }),
        ...(scope === 'brand' && brandIds.length > 0 && { brandIds }),
      })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SELLER_KEYS.all }),
  })
}

export const useRemoveWhitelistedSellerGlobal = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sellerId: string) => removeWhitelistedSellerGlobal(sellerId),
    onSuccess: () => qc.invalidateQueries({ queryKey: SELLER_KEYS.all }),
  })
}

export const useRemoveWhitelistedSellerForMember = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ memberId, sellerId }: { memberId: string; sellerId: string }) =>
      removeWhitelistedSellerForMember(memberId, sellerId),
    onSuccess: () => qc.invalidateQueries({ queryKey: SELLER_KEYS.all }),
  })
}

export const useRemoveWhitelistedSellerForBrand = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ brandId, sellerId }: { brandId: string; sellerId: string }) =>
      removeWhitelistedSellerForBrand(brandId, sellerId),
    onSuccess: () => qc.invalidateQueries({ queryKey: SELLER_KEYS.all }),
  })
}

export const useSetSellerUnderInvestigation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: SetUnderInvestigationPayload) => setSellerUnderInvestigation(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: SELLER_KEYS.all }),
  })
}

// ─── Comment Queries & Mutations ──────────────────────────────────────────────

export const SELLER_COMMENT_KEYS = {
  list: (sellerId: string) => ['sellerComments', sellerId] as const,
}

export const useSellerComments = (sellerId: string) => {
  return useQuery({
    queryKey: SELLER_COMMENT_KEYS.list(sellerId),
    queryFn: () => getSellerComments(sellerId),
    enabled: !!sellerId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  })
}

export const useCreateSellerComment = (sellerId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateSellerCommentPayload) => createSellerComment(sellerId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: SELLER_COMMENT_KEYS.list(sellerId) }),
  })
}

export const useUpdateSellerComment = (sellerId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ commentId, payload }: { commentId: string; payload: CreateSellerCommentPayload }) =>
      updateSellerComment(sellerId, commentId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: SELLER_COMMENT_KEYS.list(sellerId) }),
  })
}

export const useDeleteSellerComment = (sellerId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (commentId: string) => deleteSellerComment(sellerId, commentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: SELLER_COMMENT_KEYS.list(sellerId) }),
  })
}

// ─── Seller Item Export ───────────────────────────────────────────────────────

export const SELLER_EXPORT_KEYS = {
  status: (taskId: string | null) => ['sellerItemExport', taskId] as const,
}

export const useSellerItemExportStatus = (taskId: string | null) => {
  return useQuery({
    queryKey: SELLER_EXPORT_KEYS.status(taskId),
    queryFn: () => getSellerItemExportStatus(taskId!),
    enabled: taskId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'Completed' || status === 'Aborted') return false
      return 2000
    },
    refetchOnWindowFocus: false,
  })
}

export const useCreateSellerItemExport = () => {
  return useMutation({
    mutationFn: (data: SellerItemExportPayload) => createSellerItemExport(data),
  })
}

export const useAbortSellerItemExport = () => {
  return useMutation({
    mutationFn: (id: string) => abortSellerItemExport(id),
  })
}

export const useBulkCreateSellers = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateSellerPayload[]) => bulkCreateSellers(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: SELLER_KEYS.all }) },
  })
}
